import React, { useRef, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardContent from "../../components/dashboard/DashboardContent";
import useResponsive from "../../hooks/useResponsive";
import Header from "../../layout/dashboard/header";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useMutation } from "@tanstack/react-query";
import { apiReportSummaryCharge } from "../../api/dashboard";
import { useNotificationService } from "../../components/notification";

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");

  const dashboardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { showNotification } = useNotificationService();

  const mutation = useMutation({
    mutationFn: () => apiReportSummaryCharge(),
    onSuccess: async (data) => {
      if (data?.status === "error") {
        if ("msg" in data)
          showNotification(data?.msg ?? "Failed to deduct charge", "error");
        return;
      }

      await generatePDF();
    },
    onError: () => {
      showNotification("Failed to process request", "error");
    },
  });

  const generatePDF = async () => {
    if (!dashboardRef.current) return;

    setIsDownloading(true);

    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      const pdf = new jsPDF("p", "mm", "a4");
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const date = new Date().toISOString().split("T")[0];
      pdf.save(`dashboard-report-${date}.pdf`);

      showNotification("Report downloaded successfully", "success");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showNotification("Failed to generate PDF", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: "#2F66F4",
              }}
            >
              Dashboard
            </Typography>
          </Stack>
          {!isSmall && (
            <Stack direction={"row"} sx={{ gap: 3 }}>
              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || isDownloading}
                sx={{
                  border: `1px solid ${theme.palette.primary.main}`,
                  padding: "4px 12px",
                  height: isMedium ? "35px" : "40px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontFamily: "DM Sans",
                  minWidth: "150px",
                }}
              >
                {mutation.isPending || isDownloading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  "Download Report"
                )}
              </Button>
            </Stack>
          )}
        </Box>
      </Header>

      {/* Wrap DashboardContent with ref for PDF capture */}
      <div ref={dashboardRef}>
        <DashboardContent />
      </div>
    </>
  );
};

export default Dashboard;
